import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ToneImg from "@/components/storefront/ToneImg";
import { useTone } from "@/lib/imageTone";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

/**
 * Product photos for the product page of every theme: a main frame that shows the whole photo (never cropped),
 * clickable thumbnails to switch photos, and a full-screen viewer when the main photo is clicked.
 * `preferred` (optional) is an image to show first, e.g. the photo of the chosen variant.
 */
export default function ProductGallery({ images = [], name = "", preferred = "", c, border, muted }) {
  const list = useMemo(() => [...new Set([preferred, ...images].filter(Boolean))], [images, preferred]);
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const key = list.join("|");
  useEffect(() => { setActive(0); }, [key]);
  const tone = useTone(list[active]);
  const many = list.length > 1;
  const go = useCallback((d) => setActive((i) => (i + d + list.length) % list.length), [list.length]);

  if (list.length === 0) {
    return <div className="rounded-2xl aspect-square flex items-center justify-center text-sm" style={{ background: "#f1f5f9", border: `1px solid ${border}`, color: muted }}>No image</div>;
  }

  return (
    <div data-testid="product-gallery">
      <button type="button" onClick={() => setOpen(true)} aria-label="View larger photo" data-testid="gallery-main"
        className="group relative block w-full rounded-2xl overflow-hidden aspect-square cursor-zoom-in" style={{ background: tone ? tone.bg : "#fff", border: `1px solid ${border}` }}>
        <img key={list[active]} src={list[active]} alt={name} data-tone-skip className="pg-fade w-full h-full object-contain" style={tone ? { padding: "8%", filter: "drop-shadow(0 14px 18px rgba(0,0,0,.2))" } : undefined} />
        <span className="absolute right-3 bottom-3 w-9 h-9 rounded-full bg-white/90 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: c.text }}><ZoomIn size={17} /></span>
      </button>

      {many && (
        <div className="flex gap-2.5 mt-3 overflow-x-auto pb-1" data-testid="gallery-thumbs">
          {list.map((img, i) => (
            <button key={img} type="button" onClick={() => setActive(i)} aria-label={`Show photo ${i + 1}`} aria-current={i === active} data-testid={`gallery-thumb-${i}`}
              className="shrink-0 w-[72px] h-[72px] rounded-xl overflow-hidden bg-white transition-all hover:-translate-y-0.5"
              style={{ border: `2px solid ${i === active ? c.primary : border}`, opacity: i === active ? 1 : 0.75 }}>
              <ToneImg src={img} fit="contain" pad="6%" />
            </button>
          ))}
        </div>
      )}

      {open && <Lightbox list={list} index={active} name={name} onIndex={setActive} onClose={() => setOpen(false)} go={go} many={many} />}
    </div>
  );
}

function Lightbox({ list, index, name, onIndex, onClose, go, many }) {
  const touch = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); else if (many && e.key === "ArrowLeft") go(-1); else if (many && e.key === "ArrowRight") go(1); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [go, many, onClose]);

  return createPortal(
    <div className="pg-fade fixed inset-0 z-[100] flex flex-col" style={{ background: "rgba(8,10,14,.92)" }} role="dialog" aria-modal="true" aria-label={`${name} photos`} data-testid="gallery-lightbox"
      onClick={onClose}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { if (touch.current == null || !many) return; const dx = e.changedTouches[0].clientX - touch.current; if (Math.abs(dx) > 50) go(dx > 0 ? -1 : 1); touch.current = null; }}>
      <div className="flex items-center justify-between px-5 py-3 text-white/80 text-sm" onClick={(e) => e.stopPropagation()}>
        <span className="truncate pr-4">{name}{many ? ` · ${index + 1} / ${list.length}` : ""}</span>
        <button onClick={onClose} aria-label="Close" data-testid="gallery-close" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"><X size={20} /></button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-4 sm:px-16">
        {many && <button onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous photo" data-testid="gallery-prev" className="absolute left-2 sm:left-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"><ChevronLeft size={22} /></button>}
        <img key={list[index]} src={list[index]} alt={name} data-tone-skip className="pg-zoom max-w-full max-h-full object-contain select-none" onClick={(e) => e.stopPropagation()} draggable={false} />
        {many && <button onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next photo" data-testid="gallery-next" className="absolute right-2 sm:right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors"><ChevronRight size={22} /></button>}
      </div>
      {many && (
        <div className="flex gap-2 justify-center overflow-x-auto px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {list.map((img, i) => (
            <button key={img} onClick={() => onIndex(i)} aria-label={`Photo ${i + 1}`} className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-white/5 transition-all"
              style={{ border: `2px solid ${i === index ? "#fff" : "transparent"}`, opacity: i === index ? 1 : 0.55 }}><img src={img} alt="" className="w-full h-full object-contain" /></button>
          ))}
        </div>
      )}
    </div>,
    document.body,
  );
}
